import React, { useState, Suspense, lazy } from 'react';
import { Route, Switch } from 'wouter';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SearchModal } from '@/components/SearchModal';
import { SpoilerProvider } from '@/context/SpoilerContext';

// Lazy-loaded pages for optimal performance and code-splitting
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const WorldPage = lazy(() => import('@/pages/WorldPage').then(m => ({ default: m.WorldPage })));
const RegionDetailPage = lazy(() => import('@/pages/RegionDetailPage').then(m => ({ default: m.RegionDetailPage })));
const LocationsPage = lazy(() => import('@/pages/LocationsPage').then(m => ({ default: m.LocationsPage })));
const LocationDetailPage = lazy(() => import('@/pages/LocationDetailPage').then(m => ({ default: m.LocationDetailPage })));
const CharactersPage = lazy(() => import('@/pages/CharactersPage').then(m => ({ default: m.CharactersPage })));
const CharacterDetailPage = lazy(() => import('@/pages/CharacterDetailPage').then(m => ({ default: m.CharacterDetailPage })));
const BestiaryPage = lazy(() => import('@/pages/BestiaryPage').then(m => ({ default: m.BestiaryPage })));
const EnemyDetailPage = lazy(() => import('@/pages/EnemyDetailPage').then(m => ({ default: m.EnemyDetailPage })));
const DungeonsPage = lazy(() => import('@/pages/DungeonsPage').then(m => ({ default: m.DungeonsPage })));
const DungeonDetailPage = lazy(() => import('@/pages/DungeonDetailPage').then(m => ({ default: m.DungeonDetailPage })));
const ItemsPage = lazy(() => import('@/pages/ItemsPage').then(m => ({ default: m.ItemsPage })));
const ItemDetailPage = lazy(() => import('@/pages/ItemDetailPage').then(m => ({ default: m.ItemDetailPage })));
const SetsPage = lazy(() => import('@/pages/SetsPage').then(m => ({ default: m.SetsPage })));
const SetDetailPage = lazy(() => import('@/pages/SetDetailPage').then(m => ({ default: m.SetDetailPage })));
const QuestsPage = lazy(() => import('@/pages/QuestsPage').then(m => ({ default: m.QuestsPage })));
const QuestDetailPage = lazy(() => import('@/pages/QuestDetailPage').then(m => ({ default: m.QuestDetailPage })));
const StoryPage = lazy(() => import('@/pages/StoryPage').then(m => ({ default: m.StoryPage })));
const SearchPage = lazy(() => import('@/pages/SearchPage').then(m => ({ default: m.SearchPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

const PageLoader = () => (
  <div className="py-24 flex flex-col items-center justify-center space-y-3 text-parchment-300">
    <div className="w-8 h-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
    <span className="text-xs font-mono uppercase tracking-widest text-amber-500/80">Archiv wird geladen...</span>
  </div>
);

export const App: React.FC = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  return (
    <SpoilerProvider>
      <div className="min-h-screen flex flex-col bg-[#07080b] text-parchment-200">
        <Header onOpenSearch={() => setIsSearchOpen(true)} />

        <main id="main-content" className="flex-1 w-full">
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/" component={() => <HomePage onOpenSearch={() => setIsSearchOpen(true)} />} />
              
              {/* World & Regions */}
              <Route path="/world" component={WorldPage} />
              <Route path="/world/:slug" component={RegionDetailPage} />

              {/* Locations */}
              <Route path="/locations" component={LocationsPage} />
              <Route path="/locations/:slug" component={LocationDetailPage} />

              {/* Characters & NPCs */}
              <Route path="/characters" component={CharactersPage} />
              <Route path="/characters/:slug" component={CharacterDetailPage} />

              {/* Bestiary & Enemies */}
              <Route path="/bestiary" component={BestiaryPage} />
              <Route path="/bestiary/:slug" component={EnemyDetailPage} />

              {/* Dungeons */}
              <Route path="/dungeons" component={DungeonsPage} />
              <Route path="/dungeons/:slug" component={DungeonDetailPage} />

              {/* Items & Sets */}
              <Route path="/items" component={ItemsPage} />
              <Route path="/items/:slug" component={ItemDetailPage} />
              <Route path="/sets" component={SetsPage} />
              <Route path="/sets/:slug" component={SetDetailPage} />

              {/* Quests */}
              <Route path="/quests" component={QuestsPage} />
              <Route path="/quests/:slug" component={QuestDetailPage} />

              {/* Story & Chronicles */}
              <Route path="/story" component={StoryPage} />
              <Route path="/story/:regionId" component={StoryPage} />

              {/* Search */}
              <Route path="/search" component={SearchPage} />

              {/* 404 Fallback */}
              <Route component={NotFoundPage} />
            </Switch>
          </Suspense>
        </main>

        <Footer />
        <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      </div>
    </SpoilerProvider>
  );
};

export default App;
